import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Stepper } from '../../ui';
import { RootState } from '../../store/store';
import {
  FluxoDeAdesao,
  rotaDaEtapaDoFluxo,
  situacaoDasEtapasDaAdesao,
} from '../../utils/adesaoFluxo';

/**
 * As bolinhas do fluxo de adesão, ligadas ao domínio (at-62).
 *
 * ## Por que existe
 *
 * O `Stepper` de `src/ui` é puro: recebe `pendentes`, `desabilitadas` e
 * `aoIrPara` e desenha. Quem sabe **o que está pendente**, **o que não pode ser
 * reaberto** e **para onde cada bolinha leva** é o domínio — e as **11 telas**
 * do fluxo precisam da mesma resposta. Sem este intermediário, a regra viraria
 * 11 cópias, que é o que a convenção da at-24 proíbe.
 *
 * É a mesma relação que `ShellSaac` tem com o `AppShell` (`13-ui-kit.md`).
 *
 * ## O que ele resolve, em uma frase
 *
 * Pedido de Victor (10/09/2026), depois de ver o mesmo na cotação: a bolinha de
 * uma etapa anterior ficava **verde** só por estar atrás da atual, **afirmando**
 * que aquela etapa estava pronta sem ninguém ter conferido.
 */

interface PropsStepperAdesao {
  /** Etapa desta tela, de 1 a 6. */
  etapa: number;
  /**
   * Qual dos três fluxos esta tela atende — decide as rotas das etapas 1 a 3.
   *
   * ⚠️ **`null` para as telas da CAUDA** (`/produto`, `/termo`, `/assinatura`) e
   * para `/documento/edit`: elas servem mais de um fluxo e não têm como saber
   * qual. Ver `rotaDaEtapaDoFluxo` — inferir pelos marcadores do carro é a
   * inferência que a at-54 registrou como não exclusiva.
   */
  fluxo?: FluxoDeAdesao | null;
}

const StepperAdesao: React.FC<PropsStepperAdesao> = ({ etapa, fluxo = null }) => {
  const navigate = useNavigate();

  /*
   * Duas fatias, não o store inteiro: com `(state) => state` o componente
   * re-renderizaria a cada mudança de qualquer slice do app.
   */
  const associado = useSelector((state: RootState) => state.associado);
  const respostasDoSga = useSelector((state: RootState) => state.respostasDoSga);

  const { pendentes, desabilitadas } = situacaoDasEtapasDaAdesao(
    { associado, respostasDoSga },
    etapa,
  );

  /*
   * Etapa sem rota conhecida também não é clicável — e o motivo vira texto no
   * nome acessível do botão. É o caso das etapas 1 a 3 vistas da cauda: o
   * destino depende do fluxo, e chutar mandaria um consultor em substituição
   * para o formulário de adesão nova.
   */
  const travadas = { ...desabilitadas };
  for (let numero = 1; numero <= 6; numero += 1) {
    if (numero === etapa || travadas[numero]) continue;
    if (!rotaDaEtapaDoFluxo(fluxo, numero)) {
      travadas[numero] = 'use o botão Voltar para revisar';
    }
  }

  return (
    <Stepper
      etapa={etapa}
      pendentes={pendentes}
      desabilitadas={travadas}
      aoIrPara={(destino) => {
        const rota = rotaDaEtapaDoFluxo(fluxo, destino);
        if (rota) navigate(rota);
      }}
    />
  );
};

export default StepperAdesao;
